import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    Link,
} from '@mui/material';
import { getExpenseAssociatedInvoices } from '../../Services/ExpenseService';
import { getInvoicePdf } from '../../Services/InvoiceService';

import { COLORS } from '../../../utils/Application_Constants';

const Pill = ({ label, bg, color }) => (
    <Box
        sx={{
            px: 4,
            py: 1,
            borderRadius: '999px',
            fontSize: 12,
            fontWeight: 600,
            background: bg,
            color,
            lineHeight: '16px',
            textTransform: 'uppercase',
            display: 'inline-flex',
            alignItems: 'left',
            letterSpacing: 0.2,
            textAlign: 'left',
        }}
    >
        {label}
    </Box>
);

const CreditDeterminationDialog = ({ open, onClose, onConfirm, expense }) => {
    const [creditType, setCreditType] = useState('');
    const [touched, setTouched] = useState(false);
    const [linkedInvoices, setLinkedInvoices] = useState([]);

    const hasError = touched && !creditType;

    const handleConfirm = () => {
        if (!creditType) {
            setTouched(true);
            return;
        }
        onConfirm(creditType);
    };

    useEffect(async () => {
        /// Fetch linked invoices
        if (expense && expense.id) {
            const invoices = await getExpenseAssociatedInvoices(expense.id);
            if (invoices && invoices.length) {
                const invoiceIds = invoices.map((invoice) => ({
                    id: invoice.id,
                    invoiceNumber: invoice.invoiceNumber ?? null,
                }));
                setLinkedInvoices(invoiceIds.filter(({ invoiceNumber }) => invoiceNumber !== null));
            }
        }
    }, []);

    const openPdf = async (invoiceId) => {
        try {
            if (!invoiceId) {
                throw new Error('Invalid invoiceId parameter');
            }

            const pdf = await getInvoicePdf(invoiceId);
            const { base64Data } = pdf;

            if (!base64Data) {
                throw new Error(`PDF not received for invoice ${invoiceId}`);
            }
            const newTab = window.open('', '_blank');

            // Convert base64 string to binary Blob
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });

            const pdfUrl = URL.createObjectURL(blob);
            newTab.location.href = pdfUrl;
        } catch (error) {
            console.error(`There was an issue opening the PDF for Invoice ${invoiceId}: ${error}`);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="invoice-credit-alert-title"
            maxWidth={false}
            scroll="body"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 2,
                        p: 1.5,
                        width: 536,
                        maxWidth: '90vw',
                        overflow: 'visible',
                    },
                },
            }}
        >
            <DialogTitle id="invoice-credit-alert-title" sx={{ pb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <Box
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: COLORS.LT_MIDNIGHT75,
                                mb: 0.5,
                                textTransform: 'uppercase',
                            }}
                        >
                            From
                        </Typography>
                        <Pill
                            label="INVOICE REVIEW"
                            bg={COLORS.LT_SUNFLOWER25}
                            color={COLORS.LT_SUNFLOWER150}
                        />
                    </Box>

                    <Box
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: COLORS.LT_MIDNIGHT75,
                                mb: 0.5,
                                textTransform: 'uppercase',
                            }}
                        >
                            To
                        </Typography>
                        <Pill label="APPROVED" bg={COLORS.LT_EMERALD10} color={COLORS.LT_EMERALD} />
                    </Box>
                </Box>

                <Typography
                    variant="body2"
                    sx={{
                        mt: 4.5,
                        color: COLORS.BLACK,
                        fontWeight: 600,
                        fontSize: '22px',
                        lineHeight: '120%',
                    }}
                >
                    Processing Alert
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ overflow: 'visible' }}>
                <Typography
                    variant="body2"
                    sx={{ mb: 3, mt: 2.5, fontWeight: 400, lineHeight: '22px', fontSize: '14px' }}
                >
                    This Expense is linked to these invoices:{' '}
                    {Array.isArray(linkedInvoices) && linkedInvoices.length
                        ? linkedInvoices.map(({ id, invoiceNumber }, idx) => (
                              <Box component="span" key={id} sx={{ display: 'inline' }}>
                                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                                  <Link
                                      href="#"
                                      onClick={(e) => {
                                          e.preventDefault();
                                          openPdf(id);
                                      }}
                                      underline="hover"
                                      sx={{ color: COLORS.LT_JOURNEY, whiteSpace: 'nowrap' }}
                                      target="_blank"
                                      rel="noopener"
                                  >
                                      {invoiceNumber}
                                  </Link>
                                  {idx < linkedInvoices.length - 1 ? <span>{' | '}</span> : null}
                              </Box>
                          ))
                        : '—'}
                </Typography>

                <Box sx={{ mb: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: COLORS.BLACK,
                            fontWeight: 400,
                            display: 'inline-flex',
                            gap: 0.5,
                        }}
                    >
                        Select what type of credit you need to do:
                        <Typography component="span" sx={{ color: COLORS.LT_PEPPER }}>
                            *
                        </Typography>
                    </Typography>
                </Box>

                <RadioGroup
                    row
                    value={creditType}
                    onChange={(e) => {
                        setCreditType(e.target.value);
                        if (!touched) setTouched(true);
                    }}
                    aria-label="credit-type"
                    name="credit-type"
                    sx={{ gap: 2 }}
                >
                    <FormControlLabel
                        value="partial"
                        control={<Radio size="small" />}
                        label={<Typography variant="body2">Partial</Typography>}
                    />
                    <FormControlLabel
                        value="full"
                        control={<Radio size="small" />}
                        label={<Typography variant="body2">Full Credit</Typography>}
                    />
                </RadioGroup>

                {hasError && (
                    <Typography
                        variant="caption"
                        sx={{ color: COLORS.LT_PEPPER, display: 'block' }}
                        role="alert"
                        aria-live="assertive"
                    >
                        Please select a credit type.
                    </Typography>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    gap: 1.5,
                    justifyContent: 'flex-start',
                    pl: 3,
                    pb: 3,
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        textTransform: 'none',
                        borderRadius: '4px',
                        minWidth: 120,
                        borderColor: COLORS.LT_JOURNEY,
                        color: COLORS.LT_JOURNEY,
                        backgroundColor: COLORS.WHITE,
                        '&:hover': {
                            borderColor: COLORS.LT_JOURNEY125,
                            color: COLORS.LT_JOURNEY125,
                            backgroundColor: COLORS.WHITE,
                        },
                        '& .MuiTouchRipple-root': { color: COLORS.LT_JOURNEY },
                        py: 1,
                        px: 6,
                    }}
                >
                    Cancel
                </Button>

                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    sx={{
                        textTransform: 'none',
                        borderRadius: '4px',
                        minWidth: 120,
                        backgroundColor: COLORS.LT_JOURNEY,
                        color: COLORS.WHITE,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: COLORS.LT_JOURNEY125,
                            boxShadow: 'none',
                        },
                        py: 1,
                        px: 6,
                    }}
                >
                    Confirmed
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreditDeterminationDialog;
